import { useAuthorization } from '@frontend/authorization';
import { ObjectColumns } from '@frontend/components/list';
import { useModals } from '@frontend/modal';
import { DELETED_ROW_GRACE_PERIOD_MS, TableAutoRefreshMode, markRowPendingDelete, removeRow, useReactiveListRepository } from '@frontend/repository';
import { ColumnDef } from '@tanstack/react-table';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ReactiveObjectListProps } from './reactive-object-list.component';
import { getInitialActiveFiltersFromSearchParams, mergeMaps } from './reactive-object-list.utils';

interface ViewProps<T extends object> extends ReturnType<typeof useReactiveListRepository<T>> {
    data: T[] | null;
    error: boolean;
    columns: ColumnDef<T>[];
    changeSelectedObjects: Dispatch<SetStateAction<T[]>>;
    selectedObjects: T[];
    mergeMaps: <K, V>(map1: Map<K, V>, map2?: Map<K, V>) => Map<K, V>;
    optionsModalOpen: boolean;
    setOptionsModalOpen: Dispatch<SetStateAction<boolean>>;
    openModal: ReturnType<typeof useModals>['openModal'];
    closeModal: ReturnType<typeof useModals>['closeModal'];
    multiDeleteModalId: string;
    onAutoRefreshModeChange: (mode: TableAutoRefreshMode) => void;
}

const useReactiveObjectList = <T extends { id: string }>(props: ReactiveObjectListProps<T>): ViewProps<T> => {
    const [searchParams] = useSearchParams();
    const initialActiveFilters = useMemo(
        () =>
            getInitialActiveFiltersFromSearchParams(
                searchParams,
                props.activeFilter?.searchKeys,
                props.activeFilter?.filterKeys,
                props.activeFilter?.queryKeyMap
            ),
        [searchParams, props.activeFilter?.searchKeys, props.activeFilter?.filterKeys, props.activeFilter?.queryKeyMap]
    );
    const defaultListValues = useMemo(() => {
        if (!initialActiveFilters || Object.keys(initialActiveFilters).length === 0) return props.defaultListValues;
        return {
            ...props.defaultListValues,
            activeFilters: {
                ...(props.defaultListValues?.activeFilters ?? {}),
                ...initialActiveFilters
            }
        };
    }, [props.defaultListValues, initialActiveFilters]);

    const [preferenceAutoRefreshMode, setPreferenceAutoRefreshMode] = useState<TableAutoRefreshMode | undefined>();
    const resolvedAutoRefreshMode = props.autoRefreshMode ?? preferenceAutoRefreshMode;

    const repository = useReactiveListRepository<T>({
        fetch: (arg) => props.fetch(arg),
        dispatch: props.dispatch,
        eventListener: props.eventListener,
        autoRefreshMode: resolvedAutoRefreshMode,
        defaultListValues,
        listId: props.useStaticListId === true ? props.id : undefined
    });
    const [selectedObjects, changeSelectedObjects] = useState<T[]>([]);
    const { permissions } = useAuthorization();
    const [optionsModalOpen, setOptionsModalOpen] = useState<boolean>(false);
    const { openModal, closeModal } = useModals();
    const multiDeleteModalId = 'multi-delete-modal';

    useEffect(() => {
        props.listStoreIdCallback && props.listStoreIdCallback(repository.listId);
    }, [repository.listId, props.listStoreIdCallback]);

    const onDeleteSuccess = useCallback(
        (object: T) => {
            const id = object.id;
            props.dispatch(markRowPendingDelete({ listId: repository.listId, id }));
            setTimeout(() => props.dispatch(removeRow({ listId: repository.listId, id })), DELETED_ROW_GRACE_PERIOD_MS);
        },
        [props.dispatch, repository.listId]
    );

    const columns = useMemo(
        () =>
            ObjectColumns<T>({
                ...props.columns,
                dispatch: props.dispatch,
                selectEnabled: !!props.multiSelectOptions,
                listStoreId: repository.listId,
                permissions: permissions,
                openModal,
                closeModal,
                onDeleteSuccess
            }),
        [props.locale, permissions]
    );

    useEffect(() => {
        changeSelectedObjects(repository.selected);
        props.selectedObjects && props.selectedObjects(repository.selected);
    }, [repository.selected]);

    return {
        ...repository,
        data: repository.data,
        error: repository.error,
        columns: columns,
        selectedObjects,
        changeSelectedObjects,
        mergeMaps,
        optionsModalOpen,
        setOptionsModalOpen,
        closeModal,
        openModal,
        multiDeleteModalId,
        onAutoRefreshModeChange: setPreferenceAutoRefreshMode
    };
};

export default useReactiveObjectList;
